import { inject, injectable } from '@/inversify';
import { KEY } from '@/inversify.keys';

import type CompsetsApiService from '@/modules/compsets/compsets-api.service';
import type HelperService from '@/modules/common/services/helper.service';
import type UserService from '@/modules/user/user.service';

import LoadingModel from '../common/models/loading.model';
import type DocumentFiltersStore from '../document-filters/store/document-filters.store';
import type StoreFacade from '../common/services/store-facade';
import type CompsetsStore from './store/compsets.store';
import type HotelsService from '../hotels/hotels.service';
import type { CompsetsService as ICompsetsService, HotelCompsets } from './types';
import type CompsetModel from './models/compset.model';

@injectable()
export default class CompsetsService implements ICompsetsService {
    @inject(KEY.CompsetsApiService) private compsetsApiService!: CompsetsApiService;
    @inject(KEY.UserService) private userService!: UserService;
    @inject(KEY.StoreFacade) private storeFacade!: StoreFacade;
    @inject(KEY.HelperService) private helperService!: HelperService;
    @inject(KEY.HotelsService) private hotelsService!: HotelsService;

    private readonly storeState: CompsetsStore = this.storeFacade.getState('CompsetsStore');
    private readonly documentFiltersStoreState: DocumentFiltersStore = this.storeFacade.getState('DocumentFiltersStore');

    /* Hotel level section, methods below should be used only if viewAs === USER_LEVELS.HOTEL */
    get currentHotelsCompsets() {
        if (!this.userService.isUserLoaded || !this.userService.currentHotelId) {
            return [];
        }

        return this.getCompsets(String(this.userService.currentHotelId)).data;
    }

    get currentHotelsCompsetsLoading() {
        if (!this.userService.isUserLoaded || !this.userService.currentHotelId) {
            return new LoadingModel();
        }

        return this.getCompsets(String(this.userService.currentHotelId)).loading;
    }

    get currentHotelsActiveCompset() {
        return this.currentHotelsCompsets
            .find(compset => compset.id === this.documentFiltersStoreState.settings.compsetId) || null;
    }

    getCurrentHotelsCompset(compsetId: string) {
        return this.currentHotelsCompsets
            .find(compset => compset.id === compsetId) || null;
    }

    /* Common section: methods below can be used anywhere (hotel, cluster, chain) */
    getCompset(hotelId: string, compsetId: string | null) {
        if (!compsetId) {
            return null;
        }

        const compsets = this.getCompsets(hotelId);

        if (!compsets.data?.length) {
            return null;
        }

        return compsets.data.find(compset => compset.id === compsetId) || null;
    }

    getCompsets(hotelId: string) {
        if (!this.storeState.compsetsCollection[hotelId]) {
            this.setCompsetsInStore(hotelId, []);
        }

        this.helperService.dynamicLoading(this.storeState.compsetsCollection[hotelId].loading, () => this.loadCompsets(hotelId));

        return this.storeState.compsetsCollection[hotelId];
    }

    setCompsetsInStore(hotelId: string, compsets: CompsetModel[], compsetsLoading?: LoadingModel) {
        const loading = compsetsLoading
            || this.storeState.compsetsCollection[hotelId]?.loading
            || new LoadingModel();

        const newCompsetsData = {
            data: compsets,
            loading,
        } as HotelCompsets;

        this.storeState.compsetsCollection = {
            ...this.storeState.compsetsCollection,
            [hotelId]: newCompsetsData,
        };
    }

    isHotelsCompsetExist(hotelId: string, compsetId?: string) {
        if (!compsetId) {
            return !!this.storeState.compsetsCollection[hotelId];
        }

        const hotelCompsets = this.storeState.compsetsCollection[hotelId];

        if (!hotelCompsets) {
            return false;
        }

        return !!hotelCompsets.data.find(compset => compset.id === compsetId);
    }

    sortCompsets(compsets: CompsetModel[]) {
        return compsets.sort((a, b) => {
            if (b.isMain) {
                return 11;
            }

            if (a.isMain) {
                return -1;
            }

            if (b.type === 'median') {
                return 1;
            }

            if (a.type === 'median') {
                return -1;
            }

            return 0;
        });
    }

    private async loadCompsets(hotelId: string) {
        if (!hotelId) {
            return false;
        }

        const { compsets, hotels } = await this.compsetsApiService
            .getCompsets(Number(hotelId));

        if (compsets?.length) {
            this.setCompsetsInStore(hotelId, this.sortCompsets(compsets));
        }

        // Add hotel names and locations from compsets to hotels collection
        if (hotels) {
            const { hotelNames, hotelGeoLocations } = hotels.reduce((acc, hotel) => ({
                hotelNames: {
                    ...acc.hotelNames,
                    [hotel.id]: hotel.name,
                },
                hotelGeoLocations: {
                    ...acc.hotelGeoLocations,
                    [hotel.id]: hotel.geoLocation,
                },
            }), {} as { hotelNames: Record<string, string>, hotelGeoLocations: Record<string, { lat: number, lng: number }>, });

            this.hotelsService.addHotelNames(hotelNames);
            this.hotelsService.addHotelGeoLocations(hotelGeoLocations);
        }

        if (compsets === null || !compsets.length) {
            this.documentFiltersStoreState.settings.compsetId = null;
        } else {
            const firstMain = compsets.find(c => c.isMain);

            this.documentFiltersStoreState.settings.compsetId = firstMain
                ? firstMain.id
                : compsets[0].id;
        }

        return true;
    }
}
